/**
 * Subject properties action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import type {IPropertiesMap, SubjectPropertyRemovalMap, SubjectPropertyUpdateMap} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";

import type {DocumentStore} from "../document";

/**
 * Hook to get the setSubjectProperties action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to set properties on subjects (elements, nets, etc.)
 */
export function useSetSubjectProperties(): (propertyUpdateMap: SubjectPropertyUpdateMap) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (propertyUpdateMap: SubjectPropertyUpdateMap) => {
            documentStore.getState().setSubjectProperties(propertyUpdateMap);
        },
        [documentStore],
    );
}

/**
 * Hook to get the removeSubjectProperties action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to remove properties from subjects (elements, nets, routes)
 */
export function useRemoveSubjectProperties(): (propertyRemovalMap: SubjectPropertyRemovalMap) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (propertyRemovalMap: SubjectPropertyRemovalMap) => {
            documentStore.getState().removeSubjectProperties(propertyRemovalMap);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setDocumentProperties action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to set document-level properties
 */
export function useSetDocumentProperties(): (setProperties: IPropertiesMap) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (setProperties: IPropertiesMap) => {
            documentStore.getState().setDocumentProperties(setProperties);
        },
        [documentStore],
    );
}

/**
 * Hook to get the removeDocumentProperties action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to remove document-level properties
 */
export function useRemoveDocumentProperties(): (propertyUids: string[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (propertyUids: string[]) => {
            documentStore.getState().removeDocumentProperties(propertyUids);
        },
        [documentStore],
    );
}

/**
 * Hook to get the updatePropertyVisibility action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to update property visibility on elements
 */
export function useUpdatePropertyVisibility(): (elementIds: string[], propertyId: string, visibility: boolean) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (elementIds: string[], propertyId: string, visibility: boolean) => {
            documentStore.getState().updatePropertyVisibility(elementIds, propertyId, visibility);
        },
        [documentStore],
    );
}

/**
 * Factory function to create subject properties actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch subject property actions.
 *
 * @param documentStore - The document dispatcher from FluxServices
 * @returns An object with subject properties action methods
 */
export function createPropertiesActions(documentStore: DocumentStore) {
    return {
        setSubjectProperties: (propertyUpdateMap: SubjectPropertyUpdateMap) => {
            documentStore.getState().setSubjectProperties(propertyUpdateMap);
        },
        removeSubjectProperties: (propertyRemovalMap: SubjectPropertyRemovalMap) => {
            documentStore.getState().removeSubjectProperties(propertyRemovalMap);
        },
        setDocumentProperties: (setProperties: IPropertiesMap) => {
            documentStore.getState().setDocumentProperties(setProperties);
        },
        removeDocumentProperties: (propertyUids: string[]) => {
            documentStore.getState().removeDocumentProperties(propertyUids);
        },
        updatePropertyVisibility: (elementIds: string[], propertyId: string, visibility: boolean) => {
            documentStore.getState().updatePropertyVisibility(elementIds, propertyId, visibility);
        },
    };
}
